import { Modal, SectionLabel } from '../../design/components'
import type { ComposerOwner } from './types'
import styles from './composer.module.css'

/**
 * How to use the workbench, for someone who has just opened it.
 *
 * A composer is not self-explanatory in the way a form is: the controls are obvious and the
 * *method* is not. Nothing on the screen tells you that order matters, that a reference picture
 * does more than a paragraph describing the same thing, or that the way to get closer to what
 * you wanted is to feed the last attempt back in. Those are the four or five things that turn
 * this from a slot machine into a tool, so they are written down where they can be read.
 *
 * In a dialog rather than on the page because it is read once and then never again — pinned to
 * the screen it would cost every later visit the space it takes. The two pieces of advice that
 * are *consequential* rather than merely useful — keep people out of a depiction, start a map
 * from the depiction — also appear inline on the prompt card, because advice nobody opens is
 * advice nobody follows (`docs/web-api/phase-9-kickoff.md` §2.4).
 */
export function ComposerHelp({ owner, onClose }: { owner: ComposerOwner; onClose: () => void }) {
  return owner.kind === 'character' ? (
    <PortraitHelp onClose={onClose} />
  ) : (
    <LocationHelp onClose={onClose} />
  )
}

function PortraitHelp({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="How to compose a portrait" onClose={onClose}>
      <p>
        A prompt here is a <strong>list, read top to bottom</strong> — not a sentence. Each block
        adds words or a picture, and the order is part of what you are saying.
      </p>

      <SectionLabel tone="muted" micro>
        A good first prompt
      </SectionLabel>
      <ol className={styles.helpList}>
        <li>
          <strong>Add profile</strong> — the character&apos;s own sheet as text. Trim it to what a
          portrait can actually show: a face, a build, what they wear, what they carry. Everything
          you leave in competes for the model&apos;s attention.
        </li>
        <li>
          <strong>Add text</strong> — the art direction the sheet does not carry. Framing
          (&ldquo;waist up, three-quarter view&rdquo;), light, weather, mood.
        </li>
        <li>
          <strong>Add image</strong> or <strong>Character art</strong> — a reference. One photograph
          does more for a face than a paragraph describing it, and a picture of another character
          keeps two portraits looking like they belong to the same world.
        </li>
      </ol>

      <Shared />

      <SectionLabel tone="muted" micro>
        Results are not saved
      </SectionLabel>
      <p>
        Everything you generate lands in <strong>Results</strong> as a draft. It is not in the
        gallery, it is not on the sheet, and it goes away after a couple of hours. Press{' '}
        <strong>Save</strong> on the one that worked; discard the rest. The first image a character
        has becomes the portrait every sheet renders.
      </p>
    </Modal>
  )
}

function LocationHelp({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="How to draw a place" onClose={onClose}>
      <p>
        A prompt here is a <strong>list, read top to bottom</strong> — not a sentence. Each block
        adds words or a picture, and the order is part of what you are saying.
      </p>

      <SectionLabel tone="muted" micro>
        Two kinds of picture, two sets of rules
      </SectionLabel>
      <p>
        The <strong>type</strong> in Settings is not a filing decision — it picks the instructions
        the model is drawing under, and the same blocks produce a different picture under each.
      </p>

      <SectionLabel tone="muted" micro>
        Depictions: leave the people out
      </SectionLabel>
      <p>
        A place drawn with figures standing in it is <strong>hard to reuse</strong>. Comic panels
        put your own characters in this location later, and every person the model puts here is one
        that has to be worked around. Draw the room, not the scene.
      </p>

      <SectionLabel tone="muted" micro>
        Battle maps: start from the depiction
      </SectionLabel>
      <p>
        If this place already has a picture, add it with <strong>This place&apos;s images</strong>{' '}
        before you generate. The map then reads as the <strong>same building</strong> — same
        footprint, same furniture, same exits — instead of a second, unrelated place with the same
        name.
      </p>
      <p>
        Maps come out black and white and A4 landscape, with <strong>numbered zones</strong>,
        lettered subzones and a legend. Small places get five zones at most; a manor or a city block
        gets more, with different terrain in each. Say what you want in those terms and you will get
        it — &ldquo;the bar is one zone, the back rooms another&rdquo;.
      </p>

      <Shared />

      <SectionLabel tone="muted" micro>
        Results are not saved
      </SectionLabel>
      <p>
        Everything you generate lands in <strong>Results</strong> as a draft. It is not in the
        gallery and it goes away after a couple of hours. Press <strong>Save</strong> on the one
        that worked; discard the rest.
      </p>
      <p>
        A place holds <strong>one battle map</strong>. Saving a second replaces the first, and you
        will be asked before that happens.
      </p>
    </Modal>
  )
}

/** The parts that are true of any prompt, whatever is being drawn. */
function Shared() {
  return (
    <>
      <SectionLabel tone="muted" micro>
        Getting closer
      </SectionLabel>
      <p>
        The strongest move on this screen is <strong>Refine</strong>. It puts a result back into the
        prompt as a reference, with a line telling the model the picture is its own last attempt —
        so the next one is a revision instead of another roll. Add a block saying what to change and
        generate again.
      </p>
      <p>
        Blocks collapse to a single line. Reorder them, edit the text, or drop one entirely — the
        prompt sent is exactly the list you can see, and nothing else.
      </p>

      <SectionLabel tone="muted" micro>
        The settings
      </SectionLabel>
      <p>
        <strong>Resolution</strong> is the only one worth touching most days: 1K is right for a
        screen, 2K or 4K if the picture is going somewhere it will be seen large — or printed.
        Higher costs time.
      </p>
      <p>
        <strong>Temperature</strong> is how far the model will wander. Lower it when the prompt is
        detailed and you want it followed; raise it when you are fishing for an idea.{' '}
        <strong>Thinking</strong> set to High helps a long, busy prompt with several references, and
        does nothing much for a short one.
      </p>
    </>
  )
}
